// (C) Copyright IBM Deutschland GmbH 2021.  All rights reserved.

/***********************************************************************************************
import
***********************************************************************************************/

import {Platform, StyleSheet} from 'react-native';
import appConfig from '../config/appConfig';
import DeviceInfo from "react-native-device-info";
import {isMobile} from "react-device-detect";

// ================================================================================================
// ================================================================================================
// THEME CONFIGURATION VALUES

// default ui-settings
// default ui-settings
// default ui-settings
// default ui-settings
/*-----------------------------------------------------------------------------------*/

const defaultUiSettings = {
  /** set to TRUE if you want ot use your own logo in the banner-component. for this to
   * work, you need to place a new logo.png file into 'src/CUSTOMIZATION/images'
   */
  useCustomLogo: false,

  /** set to TRUE if the banner component should use a full-width background, that is rendered
   * behind the app-logo. if set to false, the banner will display its own background color instead.
   * the colors for the banner can be changed through the theme
   */
  useBannerBackground: true,

  /** set to TRUE if you want ot use your own logo in the banner-component. for this to
   * work, you need to place a new defaultLogoBackground.png file into 'src/CUSTOMIZATION/images'
   */
  useCustomLogoBackground: false,

  /** ui-option
	 * allows the use of an scroll-wrapper that permanently shows the scroll indicator
	(this is only used on the screens, not in the modals) */
  allowScrollIndicators: true,
};

// default colors
/*-----------------------------------------------------------------------------------*/

const defaultColors = {
  /** the primary color of the scheme. most buttons are of this color by default, as is the top banner */
  primary: '#a5c70a',
  primary_light: '#90b36f66',       // 40% alpha
  primary_very_light: '#5a78ae66',  // 40% alpha

  /** Medizin Informatik Initiative color scheme */
  mii_bright_green: '#a5c70a',    // Primary
  mii_mid_green: '#90b36f',       // Secondary
  mii_blue_green: '#6f929e',      // Secondary
  mii_blue: '#5a78ae',            // Primary
  mii_type_gray: '#6a7484',       // Primary
  mii_type_gray_70: '#939AA6',    // Primary
  mii_type_gray_30: '#C9CCD2',    // Primary

  primary_grey_main: '#808073',
  primary_blue_main: '#28508d',

  /** highlighting for a not fully completed questionnaire (yellow-ish by default) */
  secondary: '#a5c70a',

  /** highlighting for completed questionnaires, as well as some icons (green-ish by default) */
  success: '#5a78ae',

  /** mostly used for "abort"-buttons and error messages (red-ish by default) */
  alert: '#e9b449',

  /** very faint */
  accent0: '#f2f2f2',

  /** the color primarily used for disabled elements */
  accent1: '#919191',

  /** a more subtle grey which is used for borders, backgrounds and icons */
  accent2: '#DADADA',

  /** a even more subtle grey - also used for borders and backgrounds */
  accent3: '#F8F9FA',

  /** a relatively dark grey, used for most texts instead of black (more eye-friendly) */
  accent4: '#1F2532',

  copyrightNotice: '#505050',

  /** just plain white */
  white: '#F8F9FA',

  yes: '#008f00',
  no: '#8f0000',
};

// fonts
/*-----------------------------------------------------------------------------------*/

// The following object contains all font-related information the application is currently
// using. The font-names referenced here come from the imported fonts.
// They are located at '../../assets/fonts. The directory of the fonts can be updated by changing
// the file 'react-native.config.js' in the root directory.

// The numeric parameters are altered by the "scaleFontsFkt" function  located in 'appConfig.js'
// The reason for this is to have an additional method to dynamically alter font- & layout-sizes.
// The function call can be removed here, if there is no necessity for it - alternatively the function
// itself can be altered in 'appConfig.js'.
let rescaleValue = 1;
if (Platform.OS === 'web') {
    // rescaleValue = isMobile ? 1 : 0.8;
} else if (DeviceInfo.isTablet()) {
  rescaleValue = 0.7;
}
const defaultFonts = StyleSheet.create({
  header0: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(25) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(25 + 5) * (rescaleValue),
  },

  header1: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(22) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(22 + 5) * (rescaleValue),
  },

  header2: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(18) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(18 + 5) * (rescaleValue),
  },

  header3: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(14) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(14 + 5) * (rescaleValue),
  },

  title: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(24) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(24 + 5) * (rescaleValue),
  },

  title2: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(18) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(15 + 5) * (rescaleValue),
  },

  subHeader1: {
    fontFamily: 'Roboto',
    fontSize: appConfig.scaleFontsFkt(18) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(16 + 5) * (rescaleValue),
  },

  body: {
    fontFamily: 'Roboto',
    fontSize: appConfig.scaleFontsFkt(16) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(15 + 5) * (rescaleValue),
  },

  label: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(14) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(14 + 5) * (rescaleValue),
  },

  hint: {
    fontFamily: 'Roboto',
    fontSize: appConfig.scaleFontsFkt(12) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(12 + 5) * (rescaleValue),
  },

  inputPlaceholder: {
    fontFamily: 'Roboto',
    fontStyle: 'italic',
    fontSize: appConfig.scaleFontsFkt(15) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(15 + 5) * (rescaleValue),
  },

  bold: {
    fontFamily: 'Roboto-Bold',
    fontSize: appConfig.scaleFontsFkt(17) * (rescaleValue),
    lineHeight: appConfig.scaleFontsFkt(17 + 5) * (rescaleValue),
  },
});

// default values
/*-----------------------------------------------------------------------------------*/

// the following values represent different default values that are used throughout the application.

/** default values for various properties used throughout the application */
const defaultValues = {
  // background color
  defaultBackgroundColor: defaultColors.white,
  defaultParagraphTextColor: defaultColors.accent4,
  defaultTitleTextColor: defaultColors.accent4,

  // banner
  defaultBannerBackgroundColor: defaultColors.white,
  defaultBannerButtonColor: defaultColors.success,
  defaultBannerTitleColor: defaultColors.accent4,
  defaultBannerSubTitleColor: defaultColors.accent4,
  defaultStatusBarStyleIos: 'dark-content',
  defaultStatusBarStyleAndroid: 'dark-content',
  defaultStatusBarAndroidBackgroundColor: '#EFEFEF',

  // loading-spinner
  defaultSpinnerBackgroundColor: defaultColors.primary,
  defaultSpinnerColor: defaultColors.primary,

  // buttons
  defaultButtonAlignSelf: 'center',
  defaultButtonBorderRadius: 50,
  defaultButtonBorderWidth: 0,
  defaultButtonPadding: 15,
  defaultButtonWidth: '100%',

  // survey-screen
  defaultSurveyItemBackgroundColor: defaultColors.white,
  defaultSurveyItemTitleColor: defaultColors.accent4,
  defaultSurveyIconUntouchedColor: defaultColors.no,
  defaultSurveyIconTouchedColor: defaultColors.no,
  defaultSurveyIconCompletedColor: defaultColors.yes,

  // checkIn-screen
  defaultCheckInListViewTitleColor: defaultColors.accent4,
  defaultCheckInListViewSubTitleColor: defaultColors.accent4,
  defaultContainerUntouchedBackgroundColor: defaultColors.primary,
  defaultContainerTouchedBackgroundColor: defaultColors.primary,
  defaultContainerCompletedBackgroundColor: defaultColors.success,
  defaultContainerUntouchedBorderColor: defaultColors.primary_light,
  defaultContainerTouchedBorderColor: defaultColors.secondary,
  defaultContainerCompletedBorderColor: defaultColors.success,
  defaultTimeSuccessColor: defaultColors.success,
  defaultActiveTile: defaultColors.primary,
  defaultDisabledTile: defaultColors.accent2,
  defaultSendQuestionnaireButtonBackgroundColor: defaultColors.primary,

  // list-item (about screen)
  defaultListItemIconColor: defaultColors.success,
  defaultListItemBackgroundColor: defaultColors.white,

  // list-link (about screen)
  defaultListLinkIconColor: defaultColors.success,
  legalListLinkIconColor: defaultColors.success,
  defaultListLinkBackgroundColor: defaultColors.white,

  // modal
  defaultModalContentBackgroundColor: defaultColors.white,
  defaultModalTitleColor: defaultColors.accent4,
  defaultModalContentTextColor: defaultColors.accent4,
  defaultModalBottomBarBackgroundColor: defaultColors.white,
  defaultModalSeparatorBackgroundColor: defaultColors.accent2,
  defaultModalBorderColor: defaultColors.white,
  defaultSeparatorColor: defaultColors.accent2,
  defaultModalBorderRadius: 10,

  // scrollIndicator
  defaultScrollIndicatorBackgroundColor: defaultColors.primary,
  defaultScrollIndicatorIconColor: defaultColors.white,
};

// stylesheet-objects
/*-----------------------------------------------------------------------------------*/

// the following are default stylesheet-objects that are used throughout the application.
// individual changes to it (depended on the component thats using it) can be found in
// the local styleSheet variable.

//  buttons

const button = {
  borderRadius: defaultValues.defaultButtonBorderRadius,
  borderWidth: defaultValues.defaultButtonBorderWidth,
  padding: defaultValues.defaultButtonPadding,
  alignSelf: defaultValues.defaultButtonAlignSelf,
  width: defaultValues.defaultButtonWidth,
};

const buttonPrimary = {
  ...button,
  backgroundColor: defaultColors.primary,
  borderColor: defaultColors.primary,
};

const buttonAlert = {
  ...button,
  backgroundColor: defaultColors.primary,
  borderColor: defaultColors.primary_light,
};

const buttonLabel = {
  ...defaultFonts.label,
  color: defaultColors.accent4,
  alignSelf: 'center',
  textAlign: 'center',
};

/***********************************************************************************************
export
***********************************************************************************************/

export default {
  /** the colors used in the theme */
  colors: defaultColors,

  /** default ui-settings */
  ui: defaultUiSettings,

  /** default values for various properties used throughout the application */
  values: defaultValues,

  /** default values for various properties used throughout the application */
  fonts: defaultFonts,

  /** default stylesheet-objects used throughout the application */
  classes: {
    button,
    buttonAlert,
    buttonPrimary,
    buttonLabel,
  },
};
